import i18nInst from "i18next";
import Backend, { type ChainedBackendOptions } from "i18next-chained-backend";
import HttpApi from "i18next-http-backend";
import LocalStorageBackend from "i18next-localstorage-backend";
import { initReactI18next } from "react-i18next";

import {
  API_BASE,
  I18N_PUBLIC_NAMESPACE,
  LANG_DEFAULT,
  LANGS_SUPPORTED,
} from "../constants";
import { ApplicationType } from "../enums";
import { getApplicationType } from "../helpers/application.helpers";
import { apiBaseUrl } from "../helpers/env.helpers";
import { Log } from "../helpers/log.helpers";
import { getLangLocalStorage } from "../helpers/rtl.helpers";
import { normalizeUrlBasePath } from "../helpers/url.helpers";
import type { SupportedLanguage } from "../models";
import {
  fetchI18nBundle,
  getI18nLoadPath,
  parseI18nBundle,
} from "./i18n-session.helpers";
import {
  getI18nCacheExpirationMs,
  syncI18nCacheWithApiVersion,
} from "./i18n.cache";

const initialStoredLanguage: SupportedLanguage =
  getLangLocalStorage() || LANG_DEFAULT;

const apiBasePath = normalizeUrlBasePath(apiBaseUrl() || "") + API_BASE;

i18nInst
  .use(Backend)
  .use(initReactI18next)
  .init<ChainedBackendOptions>({
    load: "languageOnly",
    returnNull: false,
    partialBundledLanguages: true,
    backend: {
      backends: [
        LocalStorageBackend, // primary
        HttpApi, // fallback
      ],
      backendOptions: [
        {
          expirationTime: getI18nCacheExpirationMs(),
        },
        {
          loadPath: (_languages: string[], namespaces: string[]) =>
            getI18nLoadPath({ apiBasePath, namespace: namespaces[0] }),
          alternateFetch: (url: string, init: RequestInit) =>
            fetchI18nBundle({ url, init }),
          parse: (
            data: string,
            _languages?: string | string[],
            namespaces?: string | string[],
          ) => parseI18nBundle({ data, namespaces }),
        },
      ],
    },
    lng: initialStoredLanguage,
    fallbackLng: LANG_DEFAULT,
    supportedLngs: LANGS_SUPPORTED,
    nsSeparator: ":",
    keySeparator: ".",
    // Only the pre-login file until a session exists; enableFullTranslations switches to the full catalogue.
    ns: [I18N_PUBLIC_NAMESPACE],
    defaultNS: I18N_PUBLIC_NAMESPACE,
    fallbackNS: I18N_PUBLIC_NAMESPACE,
    debug: false, //!isProductionEnv(),
    interpolation: {
      escapeValue: false, // react already safes from xss => https://www.i18next.com/translation-function/interpolation#unescape
    },
  })
  .then(() => {
    Log.debug("Languages initialized", initialStoredLanguage);

    // The landings app reaches this bootstrap through the `@ryde/shared` barrel and then runs its
    // own (`i18n.landings.ts`), so without this guard both would sync the one shared localStorage
    // version key - one reading the core prefix, one the landings prefix. In production a single
    // host serves both prefixes, so both reads succeed, and any disagreement between them would
    // clear the cache on every load and undo the caching entirely. The app's own bootstrap owns it.
    if (getApplicationType() === ApplicationType.Landings) {
      return undefined;
    }

    return syncI18nCacheWithApiVersion(API_BASE);
  })
  .catch(err => {
    Log.error("An error occurred while initializing translations: ", err);
  });
export default i18nInst;
